import { useEffect, useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { exportPdf } from "../lib/pdf";
import { Cover, Executive } from "../components/doc/SectionIntro";
import { Infrastructure } from "../components/doc/SectionInfra";
import { AppPreview } from "../components/doc/SectionAppPreview";
import { Brand } from "../components/doc/SectionBrand";
import { Roadmap } from "../components/doc/SectionRoadmap";
import { Proposal } from "../components/doc/SectionProposal";
import { Closing } from "../components/doc/SectionClosing";
import { Simulator } from "../components/doc/SectionSimulator";
import { Recap } from "../components/doc/SectionRecap";
import { cashflow, paybackMonth } from "../data/model";

const TOC = [
  ["copertina", "", "Copertina"],
  ["executive", "01", "Executive Summary"],
  ["infrastruttura", "02", "Infrastruttura Tecnologica"],
  ["anteprima", "03", "Anteprima App e Portale"],
  ["brand", "04", "Brand Elevation"],
  ["roadmap", "05", "RoadMap e Budget Media"],
  ["proposta", "06", "Proposta Commerciale"],
  ["chiusura", "07", "Prossimi passi"],
  ["simulatore", "08", "Simulatore pubblicità"],
  ["resoconto", "09", "Resoconto A e B"],
];

const PdfButton = ({ testId, className = "" }) => {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      await exportPdf(TOC.slice(1).map(([id]) => id));
    } finally {
      setBusy(false);
    }
  };
  return (
    <button data-testid={testId} onClick={run} disabled={busy} className={`pdf-btn ${className}`}>
      {busy ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
      {busy ? "Generazione PDF…" : "Scarica PDF"}
    </button>
  );
};

const Sidebar = ({ active }) => (
  <aside className="sidebar no-print" data-testid="toc-sidebar">
    <div className="font-display text-2xl text-navy">Bella Napoli</div>
    <div className="kicker mt-1 mb-10">Piano Synapse OS</div>
    <nav className="flex flex-col gap-1">
      {TOC.map(([id, n, l]) => (
        <a key={id} href={`#${id}`} data-testid={`toc-link-${id}`} className={`toc-link ${active === id ? "toc-link--active" : ""}`}>
          <span className="w-6 text-gold font-display">{n}</span>
          {l}
        </a>
      ))}
    </nav>
    <PdfButton testId="download-pdf-btn" className="mt-10" />
  </aside>
);

const useActiveSection = (ids) => {
  const [active, setActive] = useState(ids[0]);
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((entry) => entry.isIntersecting && setActive(entry.target.id)),
      { rootMargin: "-40% 0px -55% 0px" }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, [ids]);
  return active;
};

const SECTION_IDS = TOC.map(([id]) => id);

export default function PlanDocument() {
  const active = useActiveSection(SECTION_IDS);
  const payback = paybackMonth(cashflow(60000, 90000, "A"));

  useEffect(() => {
    document.title = "Bella Napoli — Piano di Sviluppo Tecnologico e Scalabilità | PartnerTech";
  }, []);

  return (
    <div className="doc-shell">
      <Sidebar active={active} />
      <header className="mobile-bar no-print">
        <span className="font-display text-lg text-navy">Bella Napoli</span>
        <PdfButton testId="download-pdf-btn-mobile" />
      </header>
      <main className="doc-main" data-testid="plan-document">
        <Cover />
        <div className="doc-body">
          <Executive payback={payback} />
          <Infrastructure />
          <AppPreview />
          <Brand />
          <Roadmap />
          <Proposal />
          <Closing />
          <Simulator />
          <Recap />
        </div>
      </main>
    </div>
  );
}
